import { type Signal } from '@preact/signals'
import { clsx } from 'clsx'
import { type ComponentChildren } from 'preact'

interface Props {
	text: string | Signal<string>
	color?: string
	class?: string
	weight?: string
	htmlFor?: string
	children?: ComponentChildren | ComponentChildren[]
	loading?: boolean
	loadingClass?: string
	variant?:
		| 'h1'
		| 'h2'
		| 'h3'
		| 'h4'
		| 'h5'
		| 'text1'
		| 'text2'
		| 'text3'
		| 'text4'
		| 'label1'
		| 'label2'
		| 'label3'
		| 'label4'
		| 'span1'
		| 'span2'
		| 'span4'
}

const Typography = (props: Props) => {
	let loading = ''
	props.color ??= 'text-black-100'

	if (!props.variant) {
		props.variant = 'text1'
	}

	if (props.loading) {
		loading = `animate-pulse rounded-full bg-black-900 ${props.loadingClass}`
	}

	switch (props.variant) {
		case 'h1':
			return (
				// text: 30px, leading: 36px
				<h1
					class={clsx(
						'transition-text-opacity font-wix text-[1.875rem] leading-[2.25rem] duration-300',
						props.color,
						props.class,
						props.loading ? 'text-opacity-0' : 'text-opacity-100',
						loading,
						props.weight ?? 'font-bold'
					)}
				>
					{props.text}
				</h1>
			)
		case 'h2':
			return (
				// text: 24px, leading: 28.8px
				<h2
					class={clsx(
						'transition-text-opacity font-wix text-[1.5rem] leading-[1.8rem] duration-300',
						props.color,
						props.class,
						props.loading ? 'text-opacity-0' : 'text-opacity-100',
						loading,
						props.weight ?? 'font-bold'
					)}
				>
					{props.text}
				</h2>
			)
		case 'h3':
			return (
				// text: 20px, leading: 24px
				<h3
					class={clsx(
						'transition-text-opacity font-wix text-[1.25rem] leading-[1.5rem] duration-300',
						props.color,
						props.class,
						props.loading ? 'text-opacity-0' : 'text-opacity-100',
						loading,
						props.weight ?? 'font-bold'
					)}
				>
					{props.text}
				</h3>
			)
		case 'h4':
			return (
				// text: 16px, leading: 22.4px
				<h4
					class={clsx(
						'transition-text-opacity font-wix text-[1rem] leading-[1.4rem] duration-300',
						props.color,
						props.class,
						props.loading ? 'text-opacity-0' : 'text-opacity-100',
						loading,
						props.weight ?? 'font-bold'
					)}
				>
					{props.text}
				</h4>
			)
		case 'h5':
			return (
				// text: 14px, leading: 19.6px
				<h5
					class={clsx(
						'transition-text-opacity font-wix text-[0.875rem] leading-[1.225rem] duration-300',
						props.color,
						props.class,
						props.loading ? 'text-opacity-0' : 'text-opacity-100',
						loading,
						props.weight ?? 'font-bold'
					)}
				>
					{props.text}
				</h5>
			)

		default:
			let size = 'text-[1rem] leading-[1.4rem]'

			switch (props.variant) {
				case 'text1':
					// text: 16px, leading: 22.4px
					size = 'text-[1rem] leading-[1.4rem]'
					break
				case 'text2':
					// text: 14px, leading: 19.6px
					size = 'text-[0.875rem] leading-[1.225rem]'
					break
				case 'text3':
					// text: 12px, leading: 16.8px
					size = 'text-[0.75rem] leading-[1.05rem]'
					break
				case 'text4':
					// text: 10px, leading: 12px
					size = 'text-[0.625rem] leading-[0.75rem]'
					break
				case 'label1':
					// text: 16px, leading: 19.2px
					size = 'text-[1rem] leading-[1.2rem]'
					break
				case 'label2':
					// text: 14px, leading: 16.8px
					size = 'text-[0.875rem] leading-[1.05rem]'
					break
				case 'label3':
					// text: 12px, leading: 14.4px
					size = 'text-[0.75rem] leading-[0.9rem]'
					break
				case 'label4':
					// text: 10px, leading: 12px
					size = 'text-[0.625rem] leading-[0.75rem]'
					break
				case 'span1':
					// text: 16px, leading: 19.2px
					size = 'text-[1rem] leading-[1.2rem]'
					break
				case 'span2':
					// text: 14px, leading: 16.8px
					size = 'text-[0.875rem] leading-[1.05rem]'
					break
				case 'span4':
					// text: 10px, leading: 12px
					size = 'text-[0.625rem] leading-[0.75rem]'
					break
			}

			if (props.variant.startsWith('span')) {
				return (
					<span
						class={clsx(
							'transition-text-opacity font-inter font-semibold duration-300',
							size,
							props.color,
							props.class,
							props.loading ? 'text-opacity-0' : 'text-opacity-100',
							loading
						)}
						htmlFor={props.htmlFor}
					>
						{props.text}
					</span>
				)
			}

			if (props.variant.startsWith('label')) {
				return (
					<label
						class={clsx(
							'transition-text-opacity font-inter duration-300',
							size,
							props.color,
							props.class,
							props.loading ? 'text-opacity-0' : 'text-opacity-100',
							loading,
							props.weight ?? 'font-semibold'
						)}
						htmlFor={props.htmlFor}
					>
						{props.text}
						{props.children}
					</label>
				)
			}

			return (
				<p
					class={clsx(
						'transition-text-opacity font-inter duration-300',
						size,
						props.color,
						props.class,
						props.loading ? 'text-opacity-0' : 'text-opacity-100',
						loading,
						props.weight ?? 'font-normal'
					)}
				>
					{props.text}
				</p>
			)
	}
}

export { Typography }
